import { recordEvent } from '../observability/events'
import { evaluateUpdateGate } from './updateGate'

/**
 * Anúncio e ativação da versão nova do app (service worker em `waiting`).
 *
 * Fluxo: o SW novo termina de instalar (precache completo) → `announceUpdate` → o banner avisa →
 * o usuário pede → `applyUpdate` confere o sync, manda `SKIP_WAITING` e recarrega UMA vez quando o
 * SW novo assume (`controllerchange`). Nada aqui apaga cache nem recarrega por conta própria.
 *
 * Estado fora do React (store mínima + `subscribeUpdate`) porque o aviso nasce em
 * `serviceWorker.js`, antes/fora da árvore de componentes.
 */

export type UpdateSource =
  | 'installed' // SW novo terminou de instalar nesta sessão
  | 'waiting_on_boot' // já havia SW em espera quando o app abriu (aviso ignorado antes)
  | 'activated_elsewhere' // outra aba ativou a versão nova; esta segue com JS antigo

export type UpdatePhase = 'idle' | 'available' | 'applying'

export interface UpdateState {
  phase: UpdatePhase
  dismissed: boolean
  source: UpdateSource | null
}

interface WorkerLike {
  postMessage: (message: unknown) => void
}
interface RegistrationLike {
  waiting?: WorkerLike | null
}
interface ContainerLike {
  addEventListener: (type: 'controllerchange', listener: () => void) => void
  removeEventListener: (type: 'controllerchange', listener: () => void) => void
}

export type ApplyResult =
  | { ok: true; mode: 'skip_waiting' | 'reload' }
  | {
      ok: false
      reason: 'blocked' | 'not_available' | 'already_applying'
      message: string | null
    }

export interface ApplyOptions {
  /** Lê `state.sync.currentSync` na hora (não o valor do último render). Chamado 2x: no clique e após gravar o evento. */
  getSyncStatus: () => unknown
  reload?: () => void
  /** Espera máxima pelo `controllerchange` antes de voltar para "disponível". */
  timeoutMs?: number
  /** Espera máxima pela gravação do evento antes de ativar (o reload pode cortá-la). */
  persistWaitMs?: number
  /** Roda depois de o gate liberar e antes de ativar/recarregar. */
  beforeActivate?: () => void
  /** Roda se o SW novo não assumiu dentro de `timeoutMs`. */
  onTimeout?: () => void
}

export const SKIP_WAITING_MESSAGE = { type: 'SKIP_WAITING' } as const
export const DEFAULT_APPLY_TIMEOUT_MS = 10000
export const DEFAULT_PERSIST_WAIT_MS = 1000

const INITIAL: UpdateState = { phase: 'idle', dismissed: false, source: null }

let state: UpdateState = INITIAL
let registration: RegistrationLike | null = null
let guardedContainer: ContainerLike | null = null
let initiatedHere = false
let reloadOnce: (() => void) | null = null
let applyTimer: ReturnType<typeof setTimeout> | null = null
let pendingTimeoutHook: (() => void) | null = null
const listeners = new Set<() => void>()

function setState(patch: Partial<UpdateState>): void {
  state = { ...state, ...patch }
  listeners.forEach((listener) => listener())
}

export const getUpdateState = (): UpdateState => state

export function subscribeUpdate(listener: () => void): () => void {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

function defaultContainer(): ContainerLike | null {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) {
    return null
  }
  return navigator.serviceWorker as unknown as ContainerLike
}

function clearApplyTimer(): void {
  if (applyTimer !== null) {
    clearTimeout(applyTimer)
    applyTimer = null
  }
}

function onControllerChange(): void {
  if (initiatedHere) {
    // fomos nós que pedimos: recarrega uma única vez
    clearApplyTimer()
    reloadOnce?.()
    return
  }
  // outra aba ativou a versão nova: esta aba continua com o JS antigo até recarregar
  promptAgain('activated_elsewhere')
}

function guardControllerChange(container: ContainerLike | null): void {
  if (!container || guardedContainer === container) return
  guardedContainer = container
  container.addEventListener('controllerchange', onControllerChange)
}

function promptAgain(source: UpdateSource): void {
  const shouldRecord = state.phase === 'idle' || state.dismissed
  setState({ phase: 'available', dismissed: false, source })
  if (shouldRecord) {
    void recordEvent({
      level: 'info',
      category: 'outro',
      action: 'version_update_prompted',
      payload: { source },
    })
  }
}

/** Há versão nova pronta. Chamado por `serviceWorker.js` (via `onUpdate`). Nunca recarrega. */
export function announceUpdate(
  reg: RegistrationLike | null,
  source: UpdateSource,
  container: ContainerLike | null = defaultContainer(),
): void {
  registration = reg
  guardControllerChange(container)
  if (state.phase === 'applying') return
  promptAgain(source)
}

/** "Depois": some até o próximo boot (ou até outro SW novo ser anunciado). */
export function dismissUpdate(): void {
  if (state.phase !== 'available' || state.dismissed) return
  setState({ dismissed: true })
  void recordEvent({
    level: 'info',
    category: 'outro',
    action: 'version_update_dismissed',
    payload: { source: state.source },
  })
}

async function recordAppliedBeforeReload(
  payload: Record<string, unknown>,
  persistWaitMs: number,
): Promise<void> {
  let timer: ReturnType<typeof setTimeout> | undefined
  try {
    await Promise.race([
      recordEvent({
        level: 'info',
        category: 'outro',
        action: 'version_update_applied',
        payload,
      }),
      new Promise<void>((resolve) => {
        timer = setTimeout(resolve, persistWaitMs)
      }),
    ])
  } finally {
    if (timer !== undefined) clearTimeout(timer)
  }
}

/**
 * Ativa a versão nova a pedido do usuário. Reconfere o sync na hora (o gate do banner pode estar
 * desatualizado). Devolve `{ ok:false }` sem efeito colateral se bloqueado.
 */
export async function applyUpdate(opts: ApplyOptions): Promise<ApplyResult> {
  if (state.phase === 'applying') {
    return { ok: false, reason: 'already_applying', message: null }
  }
  if (state.phase !== 'available') {
    return { ok: false, reason: 'not_available', message: null }
  }
  const gate = evaluateUpdateGate(opts.getSyncStatus())
  if (!gate.allowed) {
    return { ok: false, reason: 'blocked', message: gate.message }
  }

  const reload = opts.reload ?? (() => window.location.reload())
  const waiting = registration?.waiting ?? null
  const mode =
    waiting && state.source !== 'activated_elsewhere'
      ? 'skip_waiting'
      : 'reload'

  setState({ phase: 'applying' })
  const syncStatusAtClick = opts.getSyncStatus()
  await recordAppliedBeforeReload(
    {
      source: state.source,
      mode,
      syncStatus:
        typeof syncStatusAtClick === 'number' ? syncStatusAtClick : null,
    },
    opts.persistWaitMs ?? DEFAULT_PERSIST_WAIT_MS,
  )

  // O sync pode ter começado durante a gravação do evento: reconfere antes de ativar.
  const gateAfter = evaluateUpdateGate(opts.getSyncStatus())
  if (!gateAfter.allowed) {
    setState({ phase: 'available' })
    return { ok: false, reason: 'blocked', message: gateAfter.message }
  }
  opts.beforeActivate?.()

  let reloaded = false
  reloadOnce = () => {
    if (reloaded) return
    reloaded = true
    reload()
  }

  if (mode === 'reload' || !waiting) {
    reloadOnce()
    return { ok: true, mode: 'reload' }
  }

  initiatedHere = true
  pendingTimeoutHook = opts.onTimeout ?? null
  applyTimer = setTimeout(() => {
    applyTimer = null
    initiatedHere = false
    const hook = pendingTimeoutHook
    pendingTimeoutHook = null
    setState({ phase: 'available' })
    void recordEvent({
      level: 'warn',
      category: 'outro',
      action: 'version_update_apply_timeout',
      payload: { timeoutMs: opts.timeoutMs ?? DEFAULT_APPLY_TIMEOUT_MS },
    })
    hook?.()
  }, opts.timeoutMs ?? DEFAULT_APPLY_TIMEOUT_MS)
  waiting.postMessage(SKIP_WAITING_MESSAGE)
  return { ok: true, mode: 'skip_waiting' }
}

/** Só para testes: o módulo guarda estado entre chamadas. */
export function resetUpdateAnnouncer(): void {
  clearApplyTimer()
  if (guardedContainer) {
    guardedContainer.removeEventListener('controllerchange', onControllerChange)
  }
  guardedContainer = null
  registration = null
  initiatedHere = false
  reloadOnce = null
  pendingTimeoutHook = null
  listeners.clear()
  state = INITIAL
}
